import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Pagination,
  Paper,
  Chip,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import api, { API_BASE_URL } from "../../utils/api";
import AddArchitecturalRequest from "./AddArchitecturalRequest";
import MemberArchitecturalRequestDetails from "./MemberArchitecturalRequestDetails";

const ITEMS_PER_PAGE = 5;

const cardSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const nestedCardSx = {
  p: 2,
  borderRadius: "14px",
  border: "1px solid rgba(47,99,173,0.08)",
  backgroundColor: "rgba(255,255,255,0.98)",
  boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

const getStatusChipSx = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (["approved", "completed"].includes(normalized)) {
    return { color: "#15803d", backgroundColor: "rgba(34,197,94,0.12)", fontWeight: 700 };
  }
  if (["pending", "in review", "submitted"].includes(normalized)) {
    return { color: "#b45309", backgroundColor: "rgba(245,158,11,0.12)", fontWeight: 700 };
  }
  return { color: "#2f63ad", backgroundColor: "rgba(47,99,173,0.10)", fontWeight: 700 };
};

// ─── Toolbar ──────────────────────────────────────────────────────────────────

const FilterToolbar = ({ search, onSearch, sortOrder, onSort, statuses, statusFilter, onStatusFilter }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 1.25 }}>
      <TextField
        size="small"
        placeholder="Search requests…"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: "#6b778c" }} />
            </InputAdornment>
          ),
        }}
        sx={{
          flex: "1 1 200px",
          "& .MuiOutlinedInput-root": { borderRadius: "10px" },
        }}
      />

      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Sort By</InputLabel>
        <Select
          value={sortOrder}
          label="Sort By"
          onChange={(e) => onSort(e.target.value)}
          sx={{ borderRadius: "10px" }}
        >
          <MenuItem value="newest">Newest First</MenuItem>
          <MenuItem value="oldest">Oldest First</MenuItem>
        </Select>
      </FormControl>
    </Box>

    {statuses.length > 0 && (
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        <Chip
          label="All"
          size="small"
          onClick={() => onStatusFilter(null)}
          sx={{
            fontWeight: 600,
            borderRadius: "8px",
            backgroundColor: statusFilter === null ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
            color: statusFilter === null ? "#2f63ad" : "#5f6b7a",
            border: statusFilter === null ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
          }}
        />
        {statuses.map((s) => (
          <Chip
            key={s}
            label={s}
            size="small"
            onClick={() => onStatusFilter(s === statusFilter ? null : s)}
            sx={{
              fontWeight: 600,
              borderRadius: "8px",
              backgroundColor: statusFilter === s ? "rgba(47,99,173,0.12)" : "rgba(0,0,0,0.05)",
              color: statusFilter === s ? "#2f63ad" : "#5f6b7a",
              border: statusFilter === s ? "1px solid rgba(47,99,173,0.3)" : "1px solid transparent",
            }}
          />
        ))}
      </Box>
    )}
  </Box>
);

// ─── Request card ─────────────────────────────────────────────────────────────

const RequestCard = ({ req, onView, getTypeName }) => {
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/request-files/${req.id}`)
      .then((res) => {
        if (cancelled) return;
        const img = (res.data || []).find((f) => f.file_type?.startsWith("image/"));
        if (img) {
          const raw = img.file_url || "";
          const url = raw.startsWith("http")
            ? raw
            : `${API_BASE_URL}/request-images/${encodeURIComponent(raw.split("/").pop())}`;
          setThumbUrl(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [req.id]);

  return (
    <Paper
      elevation={0}
      sx={{
        ...nestedCardSx,
        p: 0,
        flexDirection: "row",
        overflow: "hidden",
      }}
    >
      {/* Image thumbnail (left) */}
      {thumbUrl && (
        <Box
          component="img"
          src={thumbUrl}
          alt="Request"
          sx={{
            width: 110,
            minWidth: 110,
            objectFit: "cover",
            borderRadius: "14px 0 0 14px",
            flexShrink: 0,
            display: "block",
          }}
        />
      )}

      {/* Card content (right) */}
      <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1.5,
            mb: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 0.25 }}>
              Request #{req.id}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", fontWeight: 600 }}>
              {getTypeName(req.request_type)}
              {req.subtype_name ? ` • ${req.subtype_name}` : ""}
            </Typography>
          </Box>
          <Chip size="small" label={req.status || "Unknown"} sx={getStatusChipSx(req.status)} />
        </Box>

        <Typography variant="body2" sx={{ color: "#6b778c", mb: 2, flexGrow: 1 }}>
          {req.description || "No description available."}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "auto" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={onView}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
          >
            View Details
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ArchitecturalRequests = ({ accountId, propertyId }) => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const [mobileDetail, setMobileDetail] = useState({ open: false, id: null });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [allModalOpen, setAllModalOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Toolbar state
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [statusFilter, setStatusFilter] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await api.get("/architectural-request-types");
        setRequestTypes(res.data || []);
      } catch (err) {
        console.error("Error fetching request types:", err);
      }
    };
    fetchTypes();
  }, []);

  const fetchRequests = useCallback(async () => {
    if (!accountId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get(`/architectural-requests`);
      const filtered = (res.data || []).filter(
        (r) => r.property_id?.toLowerCase() === accountId.toLowerCase()
      );
      setRequests(filtered);
    } catch (err) {
      console.error("Error fetching architectural requests:", err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleOpenCreate = () => setCreateModalOpen(true);
  const handleCloseCreate = async () => { setCreateModalOpen(false); await fetchRequests(); };
  const handleOpenAll = () => setAllModalOpen(true);
  const handleCloseAll = () => { setAllModalOpen(false); setPage(1); };

  const handleSearch = (val) => { setSearch(val); setPage(1); };
  const handleSort = (val) => { setSortOrder(val); setPage(1); };
  const handleStatusFilter = (val) => { setStatusFilter(val); setPage(1); };

  const getTypeName = (id) => {
    const type = requestTypes.find((t) => Number(t.id) === Number(id));
    return type ? type.type : "N/A";
  };

  const approved = useMemo(
    () =>
      requests.filter((r) =>
        ["approved", "completed"].includes(String(r.status || "").toLowerCase())
      ).length,
    [requests]
  );

  const pending = requests.length - approved;

  // Unique statuses for filter chips
  const uniqueStatuses = useMemo(
    () => [...new Set(requests.map((r) => r.status).filter(Boolean))].sort(),
    [requests]
  );

  // Apply search + sort + status filter
  const filteredRequests = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = requests;

    if (q) {
      result = result.filter(
        (r) =>
          String(r.id).includes(q) ||
          getTypeName(r.request_type).toLowerCase().includes(q) ||
          String(r.description || "").toLowerCase().includes(q) ||
          String(r.status || "").toLowerCase().includes(q)
      );
    }

    if (statusFilter) {
      result = result.filter((r) => r.status === statusFilter);
    }

    return [...result].sort((a, b) => {
      const da = new Date(a.created_at || 0);
      const db = new Date(b.created_at || 0);
      return sortOrder === "newest" ? db - da : da - db;
    });
  }, [requests, search, sortOrder, statusFilter, requestTypes]);

  const paginatedRequests = filteredRequests.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const isFiltering = search.trim() !== "" || statusFilter !== null;
  const previewItems = isFiltering ? filteredRequests.slice(0, 4) : requests.slice(0, 2);


  return (
    <>
      <Box sx={{ width: "100%", pb: 1 }}>
        {/* Section Header */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Architectural Requests
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review requests, create new submissions, and track approval progress.
          </Typography>
        </Box>

        {/* Create Request Modal */}
        <Modal open={createModalOpen} onClose={handleCloseCreate}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "94%", md: "90%" },
              maxWidth: 800,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: { xs: 2, md: 4 },
              borderRadius: "18px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <AddArchitecturalRequest
              propertyId={propertyId}
              accountId={accountId}
              onClose={handleCloseCreate}
            />
          </Box>
        </Modal>

        {/* Summary stat cards */}
        <Box sx={{ mb: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2.5} alignItems="stretch">
              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>Total Requests</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>{requests.length}</Typography>
                    </Box>
                    <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(47,99,173,0.10)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <ArchitectureOutlinedIcon sx={{ color: "#2f63ad" }} />
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>Pending / Active</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>{pending}</Typography>
                    </Box>
                    <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(245,158,11,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <PendingActionsOutlinedIcon sx={{ color: "#d97706" }} />
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>Approved / Completed</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>{approved}</Typography>
                    </Box>
                    <Box sx={{ width: 42, height: 42, borderRadius: "12px", backgroundColor: "rgba(34,197,94,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <CheckCircleOutlineIcon sx={{ color: "#15803d" }} />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>

        {/* Preview section */}
        {!loading && (
          <>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 0.25 }}>
                Recent Requests
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.75 }}>
                A quick preview of the latest architectural requests for this account.
              </Typography>

              <FilterToolbar
                search={search}
                onSearch={handleSearch}
                sortOrder={sortOrder}
                onSort={handleSort}
                statuses={uniqueStatuses}
                statusFilter={statusFilter}
                onStatusFilter={handleStatusFilter}
              />
            </Box>

            {requests.length === 0 ? (
              <Paper elevation={0} sx={{ ...cardSx, p: 3, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                  No architectural requests found for this account.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleOpenCreate}
                  sx={{ backgroundColor: "#2f63ad", borderRadius: "10px", textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { backgroundColor: "#1f4f94" } }}
                >
                  Create Request
                </Button>
              </Paper>
            ) : filteredRequests.length === 0 ? (
              <Paper elevation={0} sx={{ ...cardSx, p: 3, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  No requests match your search or filter.
                </Typography>
              </Paper>
            ) : (
              <>
                <Grid container spacing={2.5} sx={{ mb: 2.5 }} alignItems="stretch">
                  {previewItems.map((req) => (
                    <Grid item xs={12} md={6} key={req.id}>
                      <RequestCard
                        req={req}
                        onView={() => isMobile
                          ? setMobileDetail({ open: true, id: req.id })
                          : navigate(`/home/memberspage/ArchitecturalRequests/${req.id}`, {
                              state: { propertyId: req.property_id },
                            })
                        }
                        getTypeName={getTypeName}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Box
                  sx={{
                    pt: 2,
                    pb: 1,
                    display: "flex",
                    justifyContent: { xs: "stretch", sm: "space-between" },
                    gap: 1.5,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleOpenCreate}
                    sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
                  >
                    Create Request
                  </Button>

                  {filteredRequests.length > previewItems.length && (
                    <Button
                      variant="contained"
                      startIcon={<HomeRepairServiceIcon />}
                      onClick={handleOpenAll}
                      sx={{
                        backgroundColor: "#2f63ad",
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 700,
                        boxShadow: "none",
                        "&:hover": { backgroundColor: "#1f4f94", boxShadow: "0 8px 20px rgba(47,99,173,0.22)" },
                      }}
                    >
                      View All{filteredRequests.length !== requests.length
                        ? ` ${filteredRequests.length} Matching`
                        : ""} Requests
                    </Button>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      {/* View All Dialog */}
      <Dialog
        open={allModalOpen}
        onClose={handleCloseAll}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px", boxShadow: "0 18px 60px rgba(0,0,0,0.16)" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#223142" }}>
          All Architectural Requests
          {filteredRequests.length !== requests.length && (
            <Typography component="span" variant="body2" sx={{ color: "#6b778c", ml: 1, fontWeight: 400 }}>
              ({filteredRequests.length} of {requests.length})
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers>
          <FilterToolbar
            search={search}
            onSearch={handleSearch}
            sortOrder={sortOrder}
            onSort={handleSort}
            statuses={uniqueStatuses}
            statusFilter={statusFilter}
            onStatusFilter={handleStatusFilter}
          />

          {paginatedRequests.length === 0 ? (
            <Typography variant="body2" sx={{ color: "#6b778c", textAlign: "center", py: 3 }}>
              No requests match your search or filter.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {paginatedRequests.map((req) => (
                <Grid item xs={12} key={req.id}>
                  <RequestCard
                    req={req}
                    onView={() => {
                      handleCloseAll();
                      isMobile
                        ? setMobileDetail({ open: true, id: req.id })
                        : navigate(`/home/memberspage/ArchitecturalRequests/${req.id}`, {
                            state: { propertyId: req.property_id },
                          });
                    }}
                    getTypeName={getTypeName}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", flexDirection: "column", gap: 1, p: 2 }}>
          <Pagination
            count={Math.ceil(filteredRequests.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={(_e, value) => setPage(value)}
            color="primary"
          />
          <Button onClick={handleCloseAll} sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile detail dialog */}
      <Dialog
        open={mobileDetail.open}
        onClose={() => setMobileDetail({ open: false, id: null })}
        fullScreen
        PaperProps={{ sx: { m: 0, borderRadius: 0 } }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
            backgroundColor: "rgba(255,255,255,0.95)",
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backdropFilter: "blur(4px)",
          }}
        >
          <IconButton onClick={() => setMobileDetail({ open: false, id: null })}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ overflowY: "auto" }}>
          {mobileDetail.id && (
            <MemberArchitecturalRequestDetails
              id={String(mobileDetail.id)}
              onClose={() => setMobileDetail({ open: false, id: null })}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default ArchitecturalRequests;
